<template>
  <div class="top-menu-bar">
    <div class="top-menu-container" ref="topMenuContainer">
      <ul class="top-menu-list" ref="topMenuList">
        <li v-for="menu in topLevelMenus" :key="menu.menuId" class="top-menu-item">
          <a
            :class="{ active: activeMenu === (menu.id || menu.menuId) }"
            class="top-menu-link"
            @click.prevent="selectMenu(menu)"
          >
            <div class="top-menu-icon">
              <img :src="getMenuIcon(menu)" />
            </div>
            <span v-show="showMenuName" class="top-menu-text">{{ getTranslatedMenuName(menu) }}</span>
          </a>
        </li>
      </ul>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { ref, onMounted, onUnmounted, computed, watch } from "vue";
  import { useStore } from "@/store";
  import { useRouter } from "vue-router";
  import { getTranslatedMenuName } from "@/utils/menuI18n";
  import { Menu } from "@/features/menu/types";

  const topMenuContainer = ref<HTMLElement | null>(null);
  const topMenuList = ref<HTMLElement | null>(null);
  const showMenuName = ref<boolean>(true);
  const store = useStore();
  const router = useRouter();
  const activeMenu = ref("");

  // store 초기화 상태 확인
  const isStoreReady = computed(() => {
    return store && store.getters && store.commit;
  });

  // 현재 선택된 메뉴와 동기화
  const syncActiveMenu = () => {
    if (!isStoreReady.value) return;

    const currentSelectedMenu = store.getters.currentSelectedMenu;
    if (currentSelectedMenu) {
      activeMenu.value = currentSelectedMenu;
    }
  };

  // store의 currentSelectedMenu 변경 감지
  watch(
    () => store.getters.currentSelectedMenu,
    newMenu => {
      if (newMenu && newMenu !== activeMenu.value) {
        activeMenu.value = newMenu;
      }
    },
    { immediate: true }
  );

  // 실제 데이터베이스의 레벨1 메뉴들만 사용
  const topLevelMenus = computed(() => {
    if (!isStoreReady.value) {
      return [];
    }

    const menuData = (store.getters.menu as Menu[]) || [];

    // menuLevel 기준으로 레벨1 메뉴만 정확하게 필터링
    const level1Menus = menuData
      .filter(menu => {
        return menu.menuLvl == 1;
      })
      .sort((a, b) => {
        return a.menuPriority - b.menuPriority;
      });

    return level1Menus;
  });

  // 메뉴 아이콘 가져오기
  const getMenuIcon = (menu: any) => {
    let iconPath = "";

    if (menu.mainShowIcon) {
      iconPath = `/images/${menu.mainShowIcon}`;
    } else {
      switch (menu.menuAddress) {
        case "/home":
          iconPath = "/images/icon-home.svg";
          break;
        case "/monitoring":
          iconPath = "/images/icon-monitor.svg";
          break;
        case "/robot-control":
          iconPath = "/images/icon-robot.svg";
          break;
        case "/report":
          iconPath = "/images/icon-report.svg";
          break;
        case "/common":
          iconPath = "/images/icon-management.svg";
          break;
        case "/service-management":
          iconPath = "/images/icon-service.svg";
          break;
        case "/cafe":
          iconPath = "/images/icon-cafe.svg";
          break;
        default:
          iconPath = "/images/icon-default.svg";
          break;
      }
    }

    return iconPath;
  };

  const selectMenu = (menu: any) => {
    if (!isStoreReady.value) {
      console.warn("Store가 초기화되지 않았습니다.");
      return;
    }

    // menu.id 또는 menu.menuId를 사용
    const menuId = menu.id || menu.menuId;
    activeMenu.value = menuId;

    // 상단 메뉴 선택 시 서브메뉴 첫 번째 자동 선택
    store.commit("currentSelectedMenu", menuId);

    // 서브메뉴 첫 번째도 자동 선택 (새로운 기능)
    const menuData = store.getters.menu || [];
    const subMenus = menuData.filter((subMenu: any) => {
      const parentId = subMenu.upperMenuId || subMenu.parentMenuId || subMenu.parentId;
      return String(parentId) === String(menuId);
    });

    if (subMenus.length > 0) {
      // 첫 번째 서브메뉴 자동 선택
      const firstSubMenu = subMenus[0];
      const subMenuId = firstSubMenu.menuId || firstSubMenu.id;
      store.commit("currentSelectedSubMenu", subMenuId);

      const subMenuRoute = firstSubMenu.menuAddress || firstSubMenu.menuUrl || firstSubMenu.menuPath;
      if (subMenuRoute && subMenuRoute !== window.location.pathname) {
        try {
          const fullPath = subMenuRoute.startsWith("/") ? subMenuRoute : `/${subMenuRoute}`;

          localStorage.setItem("focusedHeaderId", menuId);

          localStorage.setItem("autoMenuNavigation", "true");

          // router.push 사용하여 SPA navigation
          router.push(fullPath);
        } catch (error) {
          console.error("라우팅 오류:", error);
        }
      }
    }
  };

  // 윈도우 리사이즈 시 메뉴 텍스트 표시/숨김 처리
  const handleResize = () => {
    if (!topMenuContainer.value || !topMenuList.value) return;

    // 먼저 메뉴 텍스트를 표시한 상태로 실제 필요한 너비를 측정
    const originalShowMenuName = showMenuName.value;
    showMenuName.value = true;
    
    // DOM 업데이트를 위해 nextTick 사용
    setTimeout(() => {
      if (topMenuList.value) {
        const requiredWidth = topMenuList.value.scrollWidth;
        const availableWidth = window.innerWidth - 40; // 여백 고려
        
        if (requiredWidth > availableWidth) {
          showMenuName.value = false;
        } else {
          showMenuName.value = true;
        }
      }
    }, 0);
  };

  onMounted(() => {
    if (!isStoreReady.value) {
      console.warn("Store가 초기화되지 않았습니다.");
      return;
    }

    // 로그인 시 첫 번째 메뉴를 기본 선택
    if (topLevelMenus.value.length > 0) {
      const firstMenu = topLevelMenus.value[0];
      const firstMenuId = firstMenu.id || firstMenu.menuId;
      activeMenu.value = firstMenuId;

      // 첫 번째 메뉴 선택 상태로 설정
      store.commit("currentSelectedMenu", firstMenuId);

      // 첫 번째 메뉴의 서브메뉴 첫 번째도 자동 선택
      const menuData = store.getters.menu || [];
      const subMenus = menuData.filter((subMenu: any) => {
        const parentId = subMenu.upperMenuId || subMenu.parentMenuId || subMenu.parentId;
        return String(parentId) === String(firstMenuId);
      });

      if (subMenus.length > 0) {
        const firstSubMenu = subMenus[0];
        const subMenuId = firstSubMenu.menuId || firstSubMenu.id;
        store.commit("currentSelectedSubMenu", subMenuId);

        const subMenuRoute = firstSubMenu.menuAddress || firstSubMenu.menuUrl || firstSubMenu.menuPath;
        if (subMenuRoute && subMenuRoute !== window.location.pathname) {
          try {
            const fullPath = subMenuRoute.startsWith("/") ? subMenuRoute : `/${subMenuRoute}`;

            localStorage.setItem("focusedHeaderId", firstMenuId);

            localStorage.setItem("autoMenuNavigation", "true");

            router.push(fullPath);
          } catch (error) {
            console.error("초기 라우팅 오류:", error);
          }
        }
      }
    }

    // 초기 리사이즈 체크
    handleResize();
    
    // 윈도우 리사이즈 이벤트 리스너 등록
    window.addEventListener('resize', handleResize);
  });

  onUnmounted(() => {
    // 윈도우 리사이즈 이벤트 리스너 제거
    window.removeEventListener('resize', handleResize);
  });
</script>

<style scoped>
  /* CSS는 layout.scss로 이동했으므로 여기서는 제거 */
</style>
